/**
 * PassCanvas — the direct-manipulation pass surface (spec §Decisions 4). An
 * Apple-faithful FRONT render of the working PassDesign where every field,
 * the chrome (colors/logo), the barcode zone and the per-region "+" are click
 * targets. Presentation + hit-testing only: the host owns selection state and
 * mounts the matching inspector popover next to the canvas.
 *
 * Live marking: a field is LIVE iff its key is CONVENTIONAL for this
 * (style, group) — conventionalLiveKeys, the static serviceToDesign/palette
 * contract — shown as the amber dot. Liveness deliberately does NOT derive
 * from the loaded doc: after save + reload the doc IS the seed, so a saved
 * custom field's key would otherwise join the live set and lock its value.
 */
import type { PassDesign, PassDesignField } from '@insurwiz/wallet-core';
import type { ReactNode } from 'react';

import { useT, type MessageKey, type Translate } from '../../i18n';
import { cssAlign } from '../../lib/cssAlign';
import type { FieldGroup } from '../../lib/fieldEditing';
import type { DesignImageSlot } from '../../types';

import { layoutForStyle } from './canvasLayout';
import { conventionalLiveKeys } from './liveFieldPalette';

export type CanvasTarget =
  | { kind: 'field'; group: FieldGroup; index: number }
  | { kind: 'add'; group: FieldGroup }
  | { kind: 'chrome' }
  | { kind: 'barcode' }
  | { kind: 'back' };

export interface PassCanvasProps {
  design: PassDesign;
  selected: CanvasTarget | null;
  onSelect: (target: CanvasTarget) => void;
  /**
   * Inline value editing ON the pass: when the selected field is NOT live,
   * its value span swaps to an <input> writing through this callback (the
   * spec's "clicking any field text edits it inline"). Live fields never
   * swap — their value is system-owned (the inspector explains why).
   */
  onFieldValueChange?: (group: FieldGroup, index: number, value: string) => void;
  imageUrlFor: (slot: DesignImageSlot) => string | null;
  /**
   * Preview mode (the pass page, the studio's generate step): the same
   * Apple-faithful render with NO edit affordances — no click targets, no
   * live dots, no "+" buttons, no empty-barcode prompt — and the back of the
   * pass listed under the card instead of behind a flip button.
   */
  readOnly?: boolean;
}

// 'back' is unreachable here (renderRow/AddButton are never called with
// 'back' — the back-of-pass "+" lives in BackOfPass.tsx's own button) but
// Record<FieldGroup, MessageKey> requires it; keep it consistent with this
// file's own "Hátoldal megtekintése" button, not a second wording. (The
// deleted legacy panel's PassFieldsEditor GROUP_TITLES used the same.)
const GROUP_LABEL_KEYS: Record<FieldGroup, MessageKey> = {
  header: 'canvas.group.header',
  primary: 'canvas.group.primary',
  secondary: 'canvas.group.secondary',
  auxiliary: 'canvas.group.auxiliary',
  back: 'canvas.group.back',
};

function isSelected(sel: CanvasTarget | null, group: FieldGroup, index: number): boolean {
  return sel?.kind === 'field' && sel.group === group && sel.index === index;
}

function LiveDot({ group, fieldKey }: { group: FieldGroup; fieldKey: string }): JSX.Element {
  const t = useT();
  return (
    <span
      data-testid={`live-dot-${group}-${fieldKey}`}
      title={t('canvas.liveDotTitle')}
      className="absolute -top-1 -right-2 h-2 w-2 rounded-full bg-amber-400 ring-1 ring-amber-600/40"
    />
  );
}

function FieldButton({
  field,
  group,
  index,
  live,
  selected,
  onSelect,
  onFieldValueChange,
  fg,
  labelColor,
  big,
  readOnly,
}: {
  field: PassDesignField;
  group: FieldGroup;
  index: number;
  live: boolean;
  selected: boolean;
  onSelect: (t: CanvasTarget) => void;
  onFieldValueChange?: (group: FieldGroup, index: number, value: string) => void;
  fg: string;
  labelColor: string;
  big?: boolean;
  readOnly?: boolean;
}): JSX.Element {
  const t = useT();
  // The wrapping <button> forces `text-left` (Tailwind), so the operator's
  // textAlignment pick must be set directly on each text-bearing element
  // (inline style always wins over that class) — label, value, and the
  // inline-edit input alike (UX4 item 3).
  const textAlign = cssAlign(field.textAlignment);
  if (readOnly) {
    return (
      <span className="relative inline-block min-w-0">
        {field.label ? (
          <span
            className="block uppercase text-[9px] tracking-wider truncate"
            style={{ color: labelColor, textAlign }}
          >
            {field.label}
          </span>
        ) : null}
        <span
          className={`block truncate font-semibold ${big ? 'text-3xl' : 'text-sm'}`}
          style={{ color: fg, textAlign }}
        >
          {field.value || '—'}
        </span>
      </span>
    );
  }
  // Selected + fixed → the value swaps to an inline input ON the pass. The
  // label row stays a click target (re-select / inspector); the input is
  // auto-focused so click→type is one motion.
  const inlineEditing = selected && !live && onFieldValueChange !== undefined;
  // A freshly added custom field is { key: '', value: '' } (no label either) —
  // fall back to a readable name so it stays a findable click target instead
  // of a degenerate ' mező szerkesztése' accessible name.
  const title = field.label || field.key;
  // The label row shows the REAL label: a seeded label-less field (the
  // generic primary 'title') used to display its uppercased key ("TITLE"),
  // which is not on the pass. Only a brand-new custom field (no key yet) gets
  // the "Új mező" placeholder; a keyed label-less field keeps an empty row.
  const labelText = field.label || (field.key === '' ? t('canvas.newField') : '');
  return (
    <span
      className={`relative inline-block min-w-0 rounded px-1 -mx-1 transition ${selected ? 'ring-2 ring-amber-300' : ''}`}
    >
      <button
        type="button"
        onClick={() => onSelect({ kind: 'field', group, index })}
        aria-label={title ? t('canvas.editFieldAria', { title }) : t('canvas.editNewFieldAria')}
        className="block w-full text-left hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-amber-300 rounded"
      >
        <span
          className="block uppercase text-[9px] tracking-wider truncate"
          style={{ color: labelColor, textAlign }}
        >
          {labelText || '\u00a0'}
        </span>
        {!inlineEditing ? (
          <span
            className={`block truncate font-semibold ${big ? 'text-3xl' : 'text-sm'}`}
            style={{ color: fg, textAlign }}
          >
            {field.value || '—'}
          </span>
        ) : null}
      </button>
      {inlineEditing ? (
        <input
          aria-label={title ? t('canvas.editValueAria', { title }) : t('canvas.editNewValueAria')}
          autoFocus
          value={field.value}
          onChange={(e) => onFieldValueChange(group, index, e.target.value)}
          // The inspector popover for this same field is open alongside; its
          // document-level outside-mousedown close would otherwise fire on a
          // click INTO this input (it lives outside the popover's DOM) and
          // unmount it mid-click. Stop the mousedown before it reaches the
          // document listener.
          onMouseDown={(e) => e.stopPropagation()}
          className={`block w-full bg-transparent border-b border-amber-300/70 outline-none font-semibold ${big ? 'text-3xl' : 'text-sm'}`}
          style={{ color: fg, textAlign }}
        />
      ) : null}
      {live ? <LiveDot group={group} fieldKey={field.key} /> : null}
    </span>
  );
}

function AddButton({
  group,
  onSelect,
}: {
  group: FieldGroup;
  onSelect: (t: CanvasTarget) => void;
}): JSX.Element {
  const t = useT();
  return (
    <button
      type="button"
      onClick={() => onSelect({ kind: 'add', group })}
      aria-label={t('canvas.addFieldAria', { group: t(GROUP_LABEL_KEYS[group]) })}
      className="h-8 w-8 shrink-0 rounded-md border border-dashed border-white/40 text-white/70 hover:bg-white/10 hover:text-white text-lg leading-none"
    >
      +
    </button>
  );
}

export function PassCanvas({
  design,
  selected,
  onSelect,
  onFieldValueChange,
  imageUrlFor,
  readOnly = false,
}: PassCanvasProps): JSX.Element {
  const t: Translate = useT();
  const layout = layoutForStyle(design.style);
  const bg = design.colors?.background ?? 'rgb(0, 51, 102)';
  const fg = design.colors?.foreground ?? 'rgb(255, 255, 255)';
  const labelColor = design.colors?.label ?? fg;

  const groupRows = (group: FieldGroup): PassDesignField[] => design.fields[group] ?? [];
  const liveKeys = conventionalLiveKeys(design.style);

  const renderRow = (group: FieldGroup, big?: boolean): JSX.Element => {
    const rows = groupRows(group);
    const live = liveKeys[group];
    // canvasLayout only models a cap for 'primary' (validatePassDesign: 2 on
    // boardingPass, 1 elsewhere) — hide the "+" there once at the cap so the
    // canvas can never lead the operator into a save-time 422 it could have
    // pre-empted. header/secondary/auxiliary have no modeled cap here.
    const atCap = group === 'primary' && rows.length >= layout.primaryMax;
    return (
      <div className="flex items-end gap-x-5 gap-y-2 flex-wrap">
        {rows.map((f, i) => (
          <FieldButton
            key={`${f.key}-${i}`}
            field={f}
            group={group}
            index={i}
            live={live.has(f.key)}
            selected={isSelected(selected, group, i)}
            onSelect={onSelect}
            onFieldValueChange={onFieldValueChange}
            fg={fg}
            labelColor={labelColor}
            big={big}
            readOnly={readOnly}
          />
        ))}
        {atCap || readOnly ? null : <AddButton group={group} onSelect={onSelect} />}
      </div>
    );
  };

  const primary = groupRows('primary');
  const livePrimary = liveKeys.primary;
  const stripUrl = imageUrlFor('strip');
  const thumbUrl = imageUrlFor('thumbnail');

  return (
    <div data-testid="pass-canvas" data-style={design.style} className="relative mx-auto w-[340px]">
      <div
        className="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/10"
        style={{ background: bg, color: fg }}
      >
        {/* Chrome row — logo + logoText + header fields; clicking the logo/text opens the chrome inspector. */}
        <div className="flex items-center gap-2 px-4 pt-4 pb-2">
          <ChromeTarget
            readOnly={readOnly}
            onClick={() => onSelect({ kind: 'chrome' })}
            ariaLabel={t('canvas.chromeAria')}
          >
            {imageUrlFor('logo') ? (
              <img
                src={imageUrlFor('logo') ?? ''}
                alt="logo"
                className="h-8 w-8 rounded-md object-contain bg-white/95 p-0.5"
              />
            ) : (
              <span className="h-8 w-8 rounded-md bg-white/20 grid place-items-center text-[9px] font-bold text-white/80">
                LOG
              </span>
            )}
            <span className="text-sm font-semibold truncate" style={{ color: fg }}>
              {design.logoText || 'Insurwiz'}
            </span>
          </ChromeTarget>
          <div className="ml-auto">{renderRow('header')}</div>
        </div>

        {stripUrl ? <img src={stripUrl} alt="strip" className="w-full h-20 object-cover" /> : null}

        <div className="px-4 pb-3 space-y-3">
          {layout.originDestination && primary.length >= 2 ? (
            <div className="flex items-center justify-between gap-3">
              <span data-testid="canvas-origin" className="min-w-0">
                <FieldButton
                  field={primary[0]!}
                  group="primary"
                  index={0}
                  live={livePrimary.has(primary[0]!.key)}
                  selected={isSelected(selected, 'primary', 0)}
                  onSelect={onSelect}
                  onFieldValueChange={onFieldValueChange}
                  fg={fg}
                  labelColor={labelColor}
                  big
                  readOnly={readOnly}
                />
              </span>
              <span aria-hidden="true" className="text-xl" style={{ color: labelColor }}>
                ✈
              </span>
              <span data-testid="canvas-destination" className="min-w-0 text-right">
                <FieldButton
                  field={primary[1]!}
                  group="primary"
                  index={1}
                  live={livePrimary.has(primary[1]!.key)}
                  selected={isSelected(selected, 'primary', 1)}
                  onSelect={onSelect}
                  onFieldValueChange={onFieldValueChange}
                  fg={fg}
                  labelColor={labelColor}
                  big
                  readOnly={readOnly}
                />
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {thumbUrl ? (
                <img src={thumbUrl} alt="thumbnail" className="h-14 w-14 rounded-lg object-cover" />
              ) : null}
              <div className="flex-1 min-w-0">{renderRow('primary', true)}</div>
            </div>
          )}
          {renderRow('secondary')}
          {renderRow('auxiliary')}
        </div>

        {readOnly ? (
          design.barcode ? (
            <div className="w-full bg-black/10 px-4 py-3 flex items-center justify-center">
              <span className="bg-white rounded-md px-6 py-3 text-[10px] font-mono text-slate-900">
                ▮▯▮ {design.barcode.format.replace('PKBarcodeFormat', '')}
              </span>
            </div>
          ) : null
        ) : (
          <button
            type="button"
            onClick={() => onSelect({ kind: 'barcode' })}
            aria-label={design.barcode ? t('canvas.editBarcodeAria') : t('canvas.addBarcodeAria')}
            className="w-full bg-black/10 px-4 py-3 flex items-center justify-center hover:bg-black/20"
          >
            {design.barcode ? (
              <span className="bg-white rounded-md px-6 py-3 text-[10px] font-mono text-slate-900">
                ▮▯▮ {design.barcode.format.replace('PKBarcodeFormat', '')}
              </span>
            ) : (
              <span className="text-xs" style={{ color: labelColor }}>
                {t('canvas.noBarcode')}
              </span>
            )}
          </button>
        )}
      </div>

      {readOnly ? (
        (design.fields.back ?? []).length > 0 ? (
          <div className="mt-3">
            <p className="text-[10px] uppercase tracking-wider font-medium text-slate-500 mb-1">
              {t('canvas.group.back')}
            </p>
            <dl className="rounded-xl bg-white ring-1 ring-slate-200 divide-y divide-slate-100">
              {(design.fields.back ?? []).map((f, i) => (
                <div key={`${f.key}-${i}`} className="px-4 py-2">
                  <dt className="text-[10px] uppercase tracking-wider text-slate-500">{f.label}</dt>
                  <dd className="text-sm text-slate-900 break-words">{f.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null
      ) : (
        <button
          type="button"
          onClick={() => onSelect({ kind: 'back' })}
          className="mt-2 w-full text-center text-xs text-brand-700 hover:underline"
        >
          {t('canvas.viewBack', { n: (design.fields.back ?? []).length })}
        </button>
      )}
    </div>
  );
}

/** The logo + logoText chrome: a click target in the editor, plain in a preview. */
function ChromeTarget({
  readOnly,
  onClick,
  ariaLabel,
  children,
}: {
  readOnly: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: ReactNode;
}): JSX.Element {
  if (readOnly) return <div className="flex items-center gap-2 min-w-0">{children}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="flex items-center gap-2 rounded px-1 -mx-1 hover:bg-white/10 min-w-0"
    >
      {children}
    </button>
  );
}
